import React, { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { clearAuthSession } from '../../api/client'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import {
  ADMIN_ROLES,
  ONBOARDING_ROLES,
  ANY_AUTHENTICATED_ROLES,
  MANAGER_ROLES,
  TESTER_OR_ADMIN_ROLES,
  getStoredAuthRole,
} from '../../lib/realm'
import fanltvLogo from '../../assets/fanltv-logo.svg'
import { modelTitle, providerLinks, providerStatus, readBool } from '../../pages/v2/v2Utils'
import '../../styles/admin-v2.css'

const ADMIN_AND_AGENCY_ROLES = [...ADMIN_ROLES, 'agency']

const navItems = [
  { to: '/v2', label: 'Command', eyebrow: 'Ops', end: true, roles: ANY_AUTHENTICATED_ROLES },
  { to: '/v2/models', label: 'Models', eyebrow: 'Setup', roles: ANY_AUTHENTICATED_ROLES },
  { to: '/v2/onboarding', label: 'Onboarding', eyebrow: 'Setup', roles: ONBOARDING_ROLES },
  { to: '/v2/partner-onboarding', label: 'Partner onboarding', eyebrow: 'Acquisition', roles: ADMIN_ROLES },
  { to: '/v2/fyp', label: 'FYP Publisher', eyebrow: 'Publishing', roles: MANAGER_ROLES, hideForFanvueOnly: true },
  { to: '/v2/connections', label: 'Connections', eyebrow: 'Platforms', roles: MANAGER_ROLES },
  { to: '/v2/fans', label: 'Fans', eyebrow: 'Audience', roles: ANY_AUTHENTICATED_ROLES },
  { to: '/v2/inbox', label: 'Inbox', eyebrow: 'Chats', roles: ANY_AUTHENTICATED_ROLES },
  { to: '/v2/customs', label: 'Customs', eyebrow: 'Sales', roles: MANAGER_ROLES },
  { to: '/v2/analytics', label: 'Analytics', eyebrow: 'Stats', roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/purchases', label: 'Purchases', eyebrow: 'Revenue', roles: ANY_AUTHENTICATED_ROLES },
  { to: '/v2/automation', label: 'Automation', eyebrow: 'Bot', roles: MANAGER_ROLES },
  { to: '/v2/diagnostics', label: 'Diagnostics', eyebrow: 'Health', roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/logs', label: 'Graph logs', eyebrow: 'Runs', end: true, roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/logs/proactive', label: 'Proactive logs', eyebrow: 'Runs', roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/autotests', label: 'Autotests', eyebrow: 'QA', end: true, roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/autotests/analysis', label: 'Log analysis', eyebrow: 'QA', roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/autotests/prompts', label: 'Test prompts', eyebrow: 'QA', roles: TESTER_OR_ADMIN_ROLES },
  { to: '/v2/users', label: 'Users', eyebrow: 'Access', roles: ADMIN_ROLES },
  { to: '/v2/configuration', label: 'Graph config', eyebrow: 'Advanced', roles: ADMIN_ROLES },
  { to: '/v2/settings', label: 'Settings', eyebrow: 'Control', roles: MANAGER_ROLES },
  { to: '/v2/agency-settings', label: 'Agency tools', eyebrow: 'L4 & queues', roles: ADMIN_AND_AGENCY_ROLES },
  { to: '/v2/traffic-intelligence', label: 'Traffic Intelligence', eyebrow: 'Acquisition', roles: ADMIN_AND_AGENCY_ROLES },
]

function getPageTitle(pathname) {
  if (pathname.includes('/partner-onboarding')) return 'Partner onboarding'
  if (pathname.includes('/traffic-intelligence')) return 'Traffic Intelligence'
  if (pathname.includes('/fyp')) return 'FYP Publisher'
  if (pathname.includes('/models/')) return 'Model workspace'
  if (pathname.includes('/models')) return 'Models'
  if (pathname.includes('/onboarding')) return 'Model onboarding'
  if (pathname.includes('/connections')) return 'Connections'
  if (pathname.includes('/fans')) return 'Fans'
  if (pathname.includes('/inbox')) return 'Inbox'
  if (pathname.includes('/customs')) return 'Custom content'
  if (pathname.includes('/analytics')) return 'Analytics'
  if (pathname.includes('/purchases')) return 'Purchases'
  if (pathname.includes('/content')) return 'Content sources'
  if (pathname.includes('/automation')) return 'Automation control'
  if (pathname.includes('/diagnostics')) return 'Diagnostics'
  if (pathname.includes('/logs/proactive')) return 'Proactive logs'
  if (pathname.includes('/logs')) return 'Graph logs'
  if (pathname.includes('/autotests')) return 'Autotests'
  if (pathname.includes('/users')) return 'Users'
  if (pathname.includes('/configuration')) return 'Graph configuration'
  if (pathname.includes('/settings')) return 'Settings'
  if (pathname.includes('/agency-settings')) return 'Agency tools'
  return 'Command center'
}

export default function AdminV2Layout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const role = getStoredAuthRole()
  const {
    loading: scopeLoading,
    fanvueReviewWorkspace,
    capabilities,
    models,
    selectedModelId,
    selectedModel,
    setSelectedModelId,
  } = useWorkspaceScope()
  const hideOutOfScopeLinks = scopeLoading || fanvueReviewWorkspace || capabilities?.is_agency_member
  const staffRestricted = capabilities?.is_agency_member && !/^\/v2\/(?:models(?:\/\d+)?|fans|inbox|purchases)\/?$/.test(location.pathname)
  useEffect(() => {
    if (staffRestricted) navigate('/v2/inbox', { replace: true })
  }, [staffRestricted, location.pathname, navigate])
  const visibleNavItems = navItems.filter((item) => (
    item.roles.includes(role) && !(hideOutOfScopeLinks && item.hideForFanvueOnly)
    && (!capabilities?.is_agency_member || ['/v2/models', '/v2/fans', '/v2/inbox', '/v2/purchases'].includes(item.to))
  ))

  function handleLogout() {
    clearAuthSession()
    navigate('/login', { replace: true })
  }

  const visibleModels = Array.isArray(models) ? models : []
  const scopedModels = selectedModel ? [selectedModel] : visibleModels
  const providerSlots = scopedModels.flatMap((model) => providerLinks(model))
  const connectedProviders = providerSlots.filter((link) => providerStatus(link) === 'connected').length
  const failedProviders = providerSlots.filter((link) => ['failed', 'error', 'blocked', 'disconnected'].includes(providerStatus(link))).length
  const pausedModels = scopedModels.filter((model) => readBool(model?.bot_paused)).length
  const automationLabel = selectedModel
    ? readBool(selectedModel.bot_paused)
      ? 'Paused'
      : String(selectedModel.automation_mode || 'full_auto').replaceAll('_', ' ')
    : pausedModels
      ? `${pausedModels} paused`
      : visibleModels.length ? 'Running' : 'Not configured'
  const healthTone = failedProviders ? 'bad' : connectedProviders ? 'good' : 'warn'
  const healthLabel = failedProviders
    ? `${failedProviders} connection issue${failedProviders === 1 ? '' : 's'}`
    : connectedProviders
      ? `${connectedProviders} connected`
      : 'Not connected'
  const healthHref = TESTER_OR_ADMIN_ROLES.includes(role) && !capabilities?.is_agency_member ? '/v2/diagnostics' : '/v2/models'

  return (
    <div className="v2-shell">
      <aside className="v2-sidebar" aria-label="FanLTV operations navigation">
        <Link to="/v2" className="v2-brand" aria-label="FanLTV operations home">
          <span className="v2-brand-mark">
            <img src={fanltvLogo} alt="" aria-hidden="true" />
          </span>
          <span>
            <strong>FanLTV</strong>
            <em>Operations OS</em>
          </span>
        </Link>

        <nav className="v2-nav">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `v2-nav-link ${isActive ? 'is-active' : ''}`}
            >
              <span>{item.eyebrow}</span>
              <strong>{item.label}</strong>
            </NavLink>
          ))}
        </nav>
        {!hideOutOfScopeLinks ? <div className="v2-sidebar-card">
          <p>Classic workspace</p>
          <strong>Advanced controls remain available in Classic.</strong>
          <Link to="/old" className="v2-old-interface-link">Open Classic</Link>
        </div> : null}
      </aside>

      <main className="v2-main">
        <header className="v2-topbar">
          <div className="v2-topbar-title">
            <p className="v2-kicker">FanLTV operations</p>
            <h1>{getPageTitle(location.pathname)}</h1>
          </div>
          <div className="v2-global-scope" aria-label="Current model and automation scope">
            <label className="v2-global-model-select">
              <span>Model scope</span>
              <select
                value={selectedModelId}
                onChange={(event) => setSelectedModelId(event.target.value)}
                disabled={scopeLoading}
              >
                <option value="">All models</option>
                {visibleModels.map((model) => (
                  <option key={model.id} value={model.id}>{modelTitle(model)}</option>
                ))}
              </select>
            </label>
            <div className="v2-global-status">
              <span>Automation</span>
              <strong className={pausedModels ? 'is-warn' : 'is-good'}>{automationLabel}</strong>
            </div>
            <Link to={healthHref} className={`v2-global-status v2-global-health is-${healthTone}`}>
              <span>Health</span>
              <strong>{healthLabel}</strong>
            </Link>
          </div>
          <div className="v2-topbar-actions">
            {!hideOutOfScopeLinks ? <Link to="/old" className="v2-logout-button v2-old-topbar-link">Classic</Link> : null}
            <span className="v2-realm">{role}</span>
            <button type="button" className="v2-logout-button" onClick={handleLogout}>Sign out</button>
          </div>
        </header>
        {scopeLoading || staffRestricted ? <div className="v2-loading">Loading workspace...</div> : children || <Outlet />}
      </main>
    </div>
  )
}
